import { Grid } from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import WSDataGridNew, { IColumns } from "../../../Components/DataGrid/WSDataGridNew";
import WSButton from "../../../Components/Button/WSButton";
import { WSIcons } from "../../../commonFunctions/iIcons";
import WSFilterButtonGroup from "../../../Components/Filter/WSFilterButtonGroup";
import {
  updateMainHeader,
} from "../../../commonFunctions/CommonFunctions";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import SwipeableDrawerForm from "../../../Components/DrawyerForm/DrawyerForm";
import { useForm } from "react-hook-form";
import http from "../../../commonFunctions/httpClient";
import LabCategoryDrawer from "./LabCategoryDrawer";

interface LabCategory {
  id: string;
  name: string;
  imageUrl?: string;
  imgUrl?: string;
  actions?: string;
}

export const LabCategoryList: React.FC<any> = React.memo(() => {
  const { control, register, setValue, reset } = useForm<any>();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formComponent, setFormComponent] = useState<any>(null);
  const [drawerTitle, setDrawerTitle] = useState("Add Lab Category");
  const [isEdit, setIsEdit] = useState(false);
  const [drawerId, setDrawerId] = useState("labCategoryAdd");

  const [categories, setCategories] = useState<LabCategory[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<LabCategory[]>([]);
  const [loading, setLoading] = useState(true);

  const filters = ["All"];
  const [selectedFilter, setSelectedFilter] = useState(filters[0]);
  const [filterCounts, setFilterCounts] = useState({ All: 0 });

  useEffect(() => {
    updateMainHeader("Lab Category");
    breadCrumbsStore.updateBreadCrumbs([
      { "Lab Category": "lab-category/list" },
    ]);
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      const response: any = await http.post("lab/LabCategory/search", {});
      const data = response?.data || response?.items || response || [];
      const list: LabCategory[] = Array.isArray(data)
        ? data.map((item: any) => ({
            id: item.id || item.labCategoryId,
            name: item.name,
            imageUrl: item.imageUrl || item.imgUrl || "",
            actions: item.id || item.labCategoryId,
          }))
        : [];
      setCategories(list);
      setFilteredCategories(list);
      setFilterCounts({ All: list.length });
    } catch (error: any) {
      console.error("Error fetching lab categories:", error);
      setCategories([]);
      setFilteredCategories([]);
      setFilterCounts({ All: 0 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleFilterChange = useCallback(
    (value: string) => {
      const originalFilter = value.replace(/\(\d+\)$/, "").trim();
      setSelectedFilter(originalFilter);
      setFilteredCategories(categories);
    },
    [categories]
  );

  const getFilterLabel = useCallback(
    (filter: string) =>
      `${filter}(${filterCounts[filter as keyof typeof filterCounts] || 0})`,
    [filterCounts]
  );

  const closeDrawer = useCallback(() => {
    reset();
    setDrawerOpen(false);
    setFormComponent(null);
  }, [reset]);

  const openAddDrawer = () => {
    const formId = "labCategoryAdd";
    const form = (
      <LabCategoryDrawer
        id={formId}
        control={control}
        register={register}
        reset={reset}
        setValue={setValue}
        onCancel={closeDrawer}
        onSuccess={fetchCategories}
      />
    );
    setIsEdit(false);
    setDrawerTitle("Add Lab Category");
    setDrawerId(formId);
    setFormComponent(form);
    setTimeout(() => setDrawerOpen(true), 10);
  };

  const openEditDrawer = useCallback(
    (categoryId: string) => {
      const formId = `labCategoryEdit-${categoryId}`;
      const form = (
        <LabCategoryDrawer
          id={formId}
          control={control}
          register={register}
          reset={reset}
          setValue={setValue}
          onCancel={closeDrawer}
          onSuccess={fetchCategories}
        />
      );
      setIsEdit(true);
      setDrawerTitle("Edit Lab Category");
      setDrawerId(formId);
      setFormComponent(form);
      setTimeout(() => setDrawerOpen(true), 10);
    },
    [control, register, reset, setValue, closeDrawer, fetchCategories]
  );

  const handleDelete = useCallback(
    async (id?: string | number) => {
      if (!id || !window.confirm("Delete this category?")) return;
      try {
        await http.del(`lab/LabCategory/${id}`);
        await fetchCategories();
      } catch (error: any) {
        console.error("Delete error:", error);
        alert("Failed to delete category.");
      }
    },
    [fetchCategories]
  );

  const handleAction = useCallback(
    (action: string, row: LabCategory) => {
      if (action === "Edit") openEditDrawer(row.id);
      else if (action === "Delete") handleDelete(row.id);
    },
    [openEditDrawer, handleDelete]
  );

  const columns: IColumns[] = [
    { header: "Category Name", fieldName: "name", type: "default" },
    {
      header: "Actions",
      fieldName: "actions",
      type: "action",
      actions: ["Edit", "Delete"],
      actionCallback: (action, row) => handleAction(action, row as LabCategory),
    },
  ];

  if (loading) {
    return (
      <Grid container spacing={2} mt={1} sx={{ paddingLeft: 1 }}>
        <Grid
          item
          sm={12}
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <div>Loading lab categories...</div>
        </Grid>
      </Grid>
    );
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item sm={9}>
          <WSFilterButtonGroup
            options={filters.map((f) => getFilterLabel(f))}
            selected={getFilterLabel(selectedFilter)}
            onChange={handleFilterChange}
          />
        </Grid>
        <Grid item sm={3} display="flex" justifyContent="flex-end">
          <WSButton
            id="btnAddLabCategory"
            label="Add Category"
            startIcon={<WSIcons.AddIcon />}
            onClick={openAddDrawer}
            sx={{
              borderRadius: "999px",
              backgroundColor: "#098963!important",
              textTransform: "none",
              fontWeight: 500,
              height: "35px!important",
              fontSize: "0.875rem!important",
              paddingX: 3,
              paddingY: 1,
              "&:hover": { backgroundColor: "#087654!important" },
            }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={2} sx={{ paddingLeft: 1 }}>
        <Grid item sm={12}>
          <WSDataGridNew
            columns={columns}
            id="labCategoryGrid"
            title={`Lab Categories (${filteredCategories.length})`}
            exportCsv={true}
            data={filteredCategories}
            addButton={{ label: "Add Category", color: "secondary" }}
            onAddButtonClick={openAddDrawer}
            searchBox={{ fieldName: "name" }}
            gridHeaderSx={{
              backgroundColor: "whitesmoke",
              border: "1px solid whitesmoke",
            }}
          />
        </Grid>
      </Grid>

      <SwipeableDrawerForm
        anchor="right"
        title={drawerTitle}
        openForm={drawerOpen}
        isEdit={isEdit}
        id={drawerId}
        formComponent={formComponent}
        onClose={closeDrawer}
      />
    </>
  );
});

export default LabCategoryList;
